import { HEARING_AID_PRODUCT_TYPE } from "constants/sales";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatDateOnlyValue } from "utils/common";

/**
 * Gets the ear from a patient_product entry.
 * Checks both direct ear property and inventory_product.ear.
 */
export const getPatientProductEar = (pp: any): string | undefined => {
  return pp?.ear || pp?.inventory_product?.ear;
};

/**
 * Finds the first hearing aid line item from a sale for a specific ear.
 * Used for individual hearing aid merge tags (not table-based).
 */
export const getFirstHearingAidFromSale = (
  sale: Sale | undefined,
  earType: string
): LineItem | undefined => {
  if (!sale?.items) return undefined;

  return (sale.items as LineItem[]).find((item) => {
    if (item.product?.master_product?.type !== HEARING_AID_PRODUCT_TYPE) {
      return false;
    }

    // Check ear in multiple locations (matching line-item-row.component.tsx pattern)
    const patientProducts = (item as any).patient_products;
    const orderItem = (item as any).order_item;

    const ear =
      item.inventory_product?.ear ??
      (Array.isArray(patientProducts)
        ? getPatientProductEar(patientProducts[0])
        : undefined) ??
      orderItem?.patient_product?.ear;

    return ear === earType;
  });
};

/**
 * Gets the serial number from a line item, checking multiple possible locations.
 * Matches pattern from line-item-row.component.tsx
 */
export const getHearingAidSerialNumber = (
  item: LineItem | undefined,
  earType: string
): string => {
  if (!item) return "";

  const patientProducts = (item as any).patient_products;
  const orderItem = (item as any).order_item;

  // Check patient_products array first (matching line-item-row pattern)
  if (Array.isArray(patientProducts)) {
    for (const pp of patientProducts) {
      if (
        getPatientProductEar(pp) === earType &&
        pp.inventory_product?.serial_number
      ) {
        return pp.inventory_product.serial_number;
      }
    }
  }

  // Check direct inventory_product
  if (item.inventory_product?.serial_number) {
    return item.inventory_product.serial_number;
  }

  // Check order_item.patient_product
  if (orderItem?.patient_product?.serial_number) {
    return orderItem.patient_product.serial_number;
  }

  return item.serial_number ?? "";
};

/**
 * Gets the warranty start date (delivered_at) from a line item.
 */
export const getHearingAidWarrantyStart = (
  item: LineItem | undefined,
  _earType: string
): string => {
  if (!item) return "";

  const deliveredAt = item.delivered_at;
  return deliveredAt ? formatDateOnlyValue(deliveredAt, "MM/dd/yyyy") : "";
};

/**
 * Gets the delivery (fitting) date from a line item.
 * Delivery date and warranty start date are the same field (delivered_at);
 * this alias exists so templates can use semantically meaningful tag names.
 */
export const getHearingAidDeliveryDate = (
  item: LineItem | undefined,
  earType: string
): string => getHearingAidWarrantyStart(item, earType);

/**
 * Gets the warranty end date from a line item, checking multiple possible locations.
 */
export const getHearingAidWarrantyEnd = (
  item: LineItem | undefined,
  earType: string
): string => {
  if (!item) return "";

  const patientProducts = (item as any).patient_products;

  // Check item-level display field first
  if ((item as any).manufacturer_warranty_expiration_date_display) {
    return formatDateOnlyValue(
      (item as any).manufacturer_warranty_expiration_date_display,
      "MM/dd/yyyy"
    );
  }

  // Check patient_products array
  if (Array.isArray(patientProducts)) {
    for (const pp of patientProducts) {
      if (
        getPatientProductEar(pp) === earType &&
        pp.inventory_product?.manufacturer_warranty_expiration_date
      ) {
        return formatDateOnlyValue(
          pp.inventory_product.manufacturer_warranty_expiration_date,
          "MM/dd/yyyy"
        );
      }
    }
  }

  // Check direct inventory_product
  if (item.inventory_product?.manufacturer_warranty_expiration_date) {
    return formatDateOnlyValue(
      item.inventory_product.manufacturer_warranty_expiration_date,
      "MM/dd/yyyy"
    );
  }

  return "";
};
